import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import { useHistory } from 'react-router';

import { CircularProgress } from '@mui/material';

import useStores from 'hooks/use-stores';

const BarChart = () => {
    const history = useHistory();
    const { luceneSearchStore } = useStores();

    useEffect(() => {
        luceneSearchStore.doRequest({
            query: 'enchantments.reddit:* last 30 days order:enchantments.reddit.counter',
            fields: ['bulletinFamily', 'cvss', 'id', 'title', 'type', 'enchantments']
        });
    }, [luceneSearchStore]);

    const getGradient = useCallback((ctx, chartArea) => {
        const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
        gradient.addColorStop(0, '#FFDBA9');
        gradient.addColorStop(1, '#FCB69F');
        return gradient;
    }, []);

    const getBackground = useCallback(
        (context) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) {
                return null;
            }
            return getGradient(ctx, chartArea);
        },
        [getGradient]
    );

    const data = useMemo(() => {
        const values = luceneSearchStore.data;
        if (!values)
            return {
                labels: [],
                datasets: []
            };

        const dataItem = values
            .map((item) => ({
                item,
                counter:
                    item._source?.enchantments?.twitter?.counter ?? item._source?.enchantments?.reddit?.counter ?? null
            }))
            .filter(({ counter }) => typeof counter === 'number')
            .sort((a, b) => b.counter - a.counter)
            .slice(0, 10)
            .reduce(
                (acc, { item, counter }) => {
                    return {
                        ...acc,
                        labels: [...acc.labels, item._id],
                        data: [...acc.data, counter]
                    };
                },
                { labels: [], data: [] }
            );

        return {
            labels: dataItem.labels,
            datasets: [
                {
                    data: dataItem.data,
                    backgroundColor: getBackground,
                    barPercentage: 0.5,
                    borderRadius: 100
                }
            ]
        };
    }, [getBackground, luceneSearchStore.data]);

    const handleElementsClick = (dataset) => {
        if (!dataset.length) return;

        const index = dataset[0]._index;
        history.push(`/cve/${data.labels[index]}`);
    };

    const options = {
        scales: {
            xAxes: [
                {
                    barPercentage: 0.9,
                    ticks: {
                        display: false,
                        beginAtZero: true
                    },
                    gridLines: {
                        display: false
                    }
                }
            ],
            yAxes: [
                {
                    ticks: {
                        beginAtZero: true,
                        fontColor: '#232323'
                    },
                    gridLines: {
                        display: false
                    }
                }
            ]
        },
        title: {
            display: false
        },
        legend: {
            display: false
        },
        onHover: (event, chartElement) => {
            event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        },
        responsive: true,
        maintainAspectRatio: false
    };

    if (luceneSearchStore.loading) {
        return <CircularProgress />;
    }

    if (!data) return null;

    return <HorizontalBar onElementsClick={handleElementsClick} height={243} data={data} options={options} />;
};

export default observer(BarChart);
